import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Archive,
  Check,
  ExternalLink,
  Inbox as InboxIcon,
  LogOut,
  MailOpen,
  MessageSquareText,
  RotateCcw,
  Trash2,
  Users,
  UserPlus,
  UserX,
  X,
} from "lucide-react";
import StudyLayout from "../components/StudyLayout";
import Breadcrumbs from "../components/Breadcrumbs";
import NavigationButtons from "../components/NavigationButtons";
import Button from "../components/Button";
import Spinner from "../components/Spinner";
import { communitiesAPI, connectionsAPI } from "../services/api";
import { useToasts } from "../hooks/useToasts";
import {
  buildCommunitiesNavigationState,
  findCommunityInRouteCache,
  readCommunitiesRouteCache,
} from "../utils/communitiesRouteCache";
import {
  readInboxRouteCache,
  writeInboxRouteCache,
} from "../utils/inboxRouteCache";
import "./Inbox.css";

const formatDate = (value) => {
  if (!value) return "";
  return new Date(value).toLocaleDateString();
};

const getNotificationIcon = (type) => {
  if (type === "community_restored") return RotateCcw;
  if (type === "community_archived") return Archive;
  if (type === "community_join_requested") return UserPlus;
  if (type === "community_join_request_approved") return Check;
  if (type === "community_join_request_rejected") return X;
  if (type === "community_member_joined") return Users;
  if (type === "community_member_left") return LogOut;
  if (type === "community_member_removed") return UserX;
  if (type === "community_invite_accepted") return Users;
  if (type === "connection_note") return MessageSquareText;
  if (type === "connection_request_received") return UserPlus;
  if (type === "connection_request_accepted") return Check;
  if (type === "connection_request_rejected") return UserX;
  return InboxIcon;
};

const emptyInbox = {
  unread: [],
  read: [],
};

const Inbox = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { addToast } = useToasts();
  const initialInboxSnapshot = useMemo(
    () => location.state?.prefetchedInbox || readInboxRouteCache(),
    [location.state],
  );
  const [loading, setLoading] = useState(!initialInboxSnapshot);
  const [inbox, setInbox] = useState(
    initialInboxSnapshot?.inbox || emptyInbox,
  );
  const [sectionLimit, setSectionLimit] = useState(
    initialInboxSnapshot?.sectionLimit || 25,
  );
  const [historyLimit, setHistoryLimit] = useState(
    initialInboxSnapshot?.historyLimit || 120,
  );
  const [actionLoadingKey, setActionLoadingKey] = useState(null);
  const [pendingDeleteKey, setPendingDeleteKey] = useState(null);
  const [pendingBulkDeleteKey, setPendingBulkDeleteKey] = useState(null);

  const breadcrumbs = useMemo(
    () => [
      { label: "MySpace", path: "/myspace" },
      {
        label: (
          <span className="inbox-breadcrumb-label">
            <InboxIcon size={14} />
            <span>Inbox</span>
          </span>
        ),
        path: null,
      },
    ],
    [],
  );
  const breadcrumbElement = <Breadcrumbs path={breadcrumbs} />;

  const loadInbox = useCallback(async (showSpinner = true) => {
    try {
      if (showSpinner) setLoading(true);
      const [
        inboxResponse,
        connectionsResponse,
        connectionInboxResponse,
      ] = await Promise.all([
        communitiesAPI.getInbox(),
        connectionsAPI.getConnections("inbox"),
        connectionsAPI.getInboxConnections(),
      ]);
      const connectionItems = (connectionsResponse.data?.connections || []).map(
        (connection) => ({
          ...connection,
          itemKind: "connection",
          inboxStatus: connection.inboxStatus || "read",
        }),
      );
      const connectionNotificationItems = (
        connectionInboxResponse.data?.notifications || []
      ).map((notification) => ({
        ...notification,
        itemKind: "connectionNotification",
        inboxStatus: notification.inboxStatus || "read",
      }));
      const nextInbox = {
        unread: [
          ...connectionItems.filter((connection) => connection.isUnread),
          ...connectionNotificationItems.filter(
            (notification) => notification.inboxStatus === "unread",
          ),
          ...(inboxResponse.data?.unread || []),
        ],
        read: [
          ...connectionItems.filter((connection) => !connection.isUnread),
          ...connectionNotificationItems.filter(
            (notification) => notification.inboxStatus !== "unread",
          ),
          ...(inboxResponse.data?.read || []),
        ],
      };
      const nextSectionLimit = inboxResponse.data?.sectionLimit || 25;
      const nextHistoryLimit = inboxResponse.data?.historyLimit || 120;

      setInbox(nextInbox);
      setSectionLimit(nextSectionLimit);
      setHistoryLimit(nextHistoryLimit);
      writeInboxRouteCache({
        inbox: nextInbox,
        sectionLimit: nextSectionLimit,
        historyLimit: nextHistoryLimit,
      });
    } catch (error) {
      addToast(
        error.response?.data?.details?.[0] || "Failed to load inbox",
        "error-warning",
      );
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    loadInbox(!initialInboxSnapshot);
    if (initialInboxSnapshot) {
      window.history.replaceState({}, "");
    }
  }, [initialInboxSnapshot, loadInbox]);

  const writeCurrentInboxCache = (nextInbox) => {
    writeInboxRouteCache({
      inbox: nextInbox,
      sectionLimit,
      historyLimit,
    });
  };

  const notifyInboxChanged = (unreadCount) => {
    const event =
      typeof unreadCount === "number"
        ? new CustomEvent("inbox-changed", {
            detail: { unreadCount: Math.max(0, unreadCount) },
          })
        : new Event("inbox-changed");
    const invitesEvent =
      typeof unreadCount === "number"
        ? new CustomEvent("invites-changed", {
            detail: { unreadCount: Math.max(0, unreadCount) },
          })
        : new Event("invites-changed");

    window.dispatchEvent(invitesEvent);
    window.dispatchEvent(event);
  };

  const replaceInboxItem = (item, nextStatus) => {
    setInbox((prev) => {
      const next = {
        unread: prev.unread.filter((entry) => entry._id !== item._id),
        read: prev.read.filter((entry) => entry._id !== item._id),
      };
      next[nextStatus] = [
        { ...item, inboxStatus: nextStatus },
        ...next[nextStatus],
      ].slice(0, sectionLimit);
      writeCurrentInboxCache(next);
      queueMicrotask(() => notifyInboxChanged(next.unread.length));
      return next;
    });
  };

  const removeInboxItem = (item) => {
    setInbox((prev) => {
      const next = {
        unread: prev.unread.filter((entry) => entry._id !== item._id),
        read: prev.read.filter((entry) => entry._id !== item._id),
      };
      writeCurrentInboxCache(next);
      queueMicrotask(() => notifyInboxChanged(next.unread.length));
      return next;
    });
  };

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const handleAcceptInvite = async (invite) => {
    try {
      setActionLoadingKey(`accept:${invite._id}`);
      await communitiesAPI.acceptInvite(invite._id);
      removeInboxItem(invite);
      addToast("Invite accepted", "success-info");
      navigate(`/communities/${invite.communityId}`);
    } catch (error) {
      addToast(
        error.response?.data?.details?.[0] || "Failed to accept invite",
        "error-warning",
      );
    } finally {
      setActionLoadingKey(null);
    }
  };

  const handleMarkInviteRead = async (invite) => {
    try {
      setActionLoadingKey(`read-invite:${invite._id}`);
      await communitiesAPI.markInviteRead(invite._id);
      replaceInboxItem(invite, "read");
    } catch (error) {
      addToast(
        error.response?.data?.details?.[0] || "Failed to mark invite read",
        "error-warning",
      );
    } finally {
      setActionLoadingKey(null);
    }
  };

  const handleDeleteInvite = async (invite) => {
    try {
      setActionLoadingKey(`delete-invite:${invite._id}`);
      await communitiesAPI.deleteInvite(invite._id);
      removeInboxItem(invite);
      setPendingDeleteKey(null);
    } catch (error) {
      addToast(
        error.response?.data?.details?.[0] || "Failed to delete invite",
        "error-warning",
      );
    } finally {
      setActionLoadingKey(null);
    }
  };

  const handleMarkNotificationRead = async (notification) => {
    try {
      setActionLoadingKey(`read:${notification._id}`);
      await communitiesAPI.markNotificationsRead([notification._id]);
      replaceInboxItem(notification, "read");
    } catch (error) {
      addToast(
        error.response?.data?.details?.[0] || "Failed to update inbox",
        "error-warning",
      );
    } finally {
      setActionLoadingKey(null);
    }
  };

  const handleDeleteNotification = async (notification) => {
    try {
      setActionLoadingKey(`delete:${notification._id}`);
      await communitiesAPI.deleteNotification(notification._id);
      removeInboxItem(notification);
      setPendingDeleteKey(null);
    } catch (error) {
      addToast(
        error.response?.data?.details?.[0] || "Failed to delete inbox item",
        "error-warning",
      );
    } finally {
      setActionLoadingKey(null);
    }
  };

  const handleMarkConnectionRead = async (connection) => {
    try {
      setActionLoadingKey(`read-connection:${connection._id}`);
      const response = await connectionsAPI.markConnectionRead(connection._id);
      replaceInboxItem(
        {
          ...(response.data?.connection || connection),
          itemKind: "connection",
        },
        "read",
      );
    } catch (error) {
      addToast(
        error.response?.data?.details?.[0] || "Failed to update connection",
        "error-warning",
      );
    } finally {
      setActionLoadingKey(null);
    }
  };

  const handleMarkConnectionNotificationRead = async (notification) => {
    try {
      setActionLoadingKey(`read-connection-notification:${notification._id}`);
      await connectionsAPI.markConnectionNotificationsRead([notification._id]);
      replaceInboxItem(notification, "read");
    } catch (error) {
      addToast(
        error.response?.data?.details?.[0] ||
          "Failed to update connection notification",
        "error-warning",
      );
    } finally {
      setActionLoadingKey(null);
    }
  };

  const handleDeleteConnectionNotification = async (notification) => {
    try {
      setActionLoadingKey(`delete-connection-notification:${notification._id}`);
      await connectionsAPI.deleteConnectionNotification(notification._id);
      removeInboxItem(notification);
      setPendingDeleteKey(null);
    } catch (error) {
      addToast(
        error.response?.data?.details?.[0] ||
          "Failed to delete connection notification",
        "error-warning",
      );
    } finally {
      setActionLoadingKey(null);
    }
  };

  const handleDeleteReadMessages = async () => {
    try {
      setActionLoadingKey("delete-read");
      await Promise.all([
        communitiesAPI.deleteReadInbox(),
        connectionsAPI.deleteReadConnectionNotifications(),
      ]);
      setInbox((prev) => {
        const next = {
          unread: prev.unread,
          read: [],
        };
        writeCurrentInboxCache(next);
        queueMicrotask(() => notifyInboxChanged(next.unread.length));
        return next;
      });
      setPendingBulkDeleteKey(null);
    } catch (error) {
      addToast(
        error.response?.data?.details?.[0] ||
          "Failed to delete read inbox messages",
        "error-warning",
      );
    } finally {
      setActionLoadingKey(null);
    }
  };

  const getViewTarget = (item, isInvite) => {
    if (isInvite) return "/communities#my-invites";
    if (item.itemKind === "connection") return `/connections/${item._id}`;
    if (item.itemKind === "connectionNotification") {
      if (item.type === "connection_request_received") return "/connections";
      return item.connectionId ? `/connections/${item.connectionId}` : "/connections";
    }
    if (item.type === "community_join_requested") {
      return `/communities/${item.communityId}#join-requests`;
    }
    return `/communities/${item.communityId}`;
  };

  const getViewState = (item, isInvite) => {
    if (isInvite) {
      return buildCommunitiesNavigationState("/inbox");
    }

    if (item.itemKind === "connection") {
      return {};
    }
    if (item.itemKind === "connectionNotification") {
      return {};
    }

    const cachedCommunity = findCommunityInRouteCache(item.communityId);
    const communitiesSnapshot = readCommunitiesRouteCache();

    return {
      fromCommunitiesSnapshot: communitiesSnapshot,
      prefetchedCommunity:
        cachedCommunity ||
        (item.communityId
          ? {
              _id: item.communityId,
              name: item.communityName || "Community",
              description: "",
            }
          : null),
    };
  };

  const handleViewItem = async (item, isInvite) => {
    const target = getViewTarget(item, isInvite);
    const state = getViewState(item, isInvite);

    if (item.inboxStatus !== "unread") {
      navigate(target, { state });
      return;
    }

    replaceInboxItem(item, "read");
    navigate(target, { state });

    try {
      if (isInvite) {
        await communitiesAPI.markInviteRead(item._id);
      } else if (item.itemKind === "connection") {
        await connectionsAPI.markConnectionRead(item._id);
      } else if (item.itemKind === "connectionNotification") {
        await connectionsAPI.markConnectionNotificationsRead([item._id]);
      } else {
        await communitiesAPI.markNotificationsRead([item._id]);
      }
    } catch (error) {
      addToast(
        error.response?.data?.details?.[0] || "Failed to update inbox",
        "error-warning",
      );
    }
  };

  if (loading) {
    return (
      <StudyLayout
        breadcrumbs={breadcrumbElement}
        buttons={<NavigationButtons />}
      >
        <div className="inbox-page">
          <Spinner visible={true} />
        </div>
      </StudyLayout>
    );
  }

  const isEmpty = inbox.unread.length === 0 && inbox.read.length === 0;

  const renderDeleteControls = (item) => {
    const deleteKey = `${item.itemKind}:${item._id}`;
    const actionKey =
      item.itemKind === "invite"
        ? `delete-invite:${item._id}`
        : item.itemKind === "connection"
          ? `read-connection:${item._id}`
        : item.itemKind === "connectionNotification"
          ? `delete-connection-notification:${item._id}`
        : `delete:${item._id}`;
    const handleDelete =
      item.itemKind === "invite"
        ? () => handleDeleteInvite(item)
        : item.itemKind === "connection"
          ? () => removeInboxItem(item)
        : item.itemKind === "connectionNotification"
          ? () => handleDeleteConnectionNotification(item)
        : () => handleDeleteNotification(item);

    return (
      <>
        <div className="inbox-delete-slot">
          <Button
            className="inbox-round-button inbox-round-button-danger"
            onClick={() => {
              if (pendingDeleteKey === deleteKey) {
                setPendingDeleteKey(null);
                return;
              }
              setPendingDeleteKey(deleteKey);
            }}
            disabled={actionLoadingKey === actionKey}
            title={
              pendingDeleteKey === deleteKey
                ? "Cancel delete"
                : "Delete permanently"
            }
            aria-label={
              pendingDeleteKey === deleteKey
                ? "Cancel delete"
                : "Delete permanently"
            }
          >
            {pendingDeleteKey === deleteKey ? (
              <X size={15} />
            ) : (
              <Trash2 size={15} />
            )}
          </Button>
        </div>
        <div className="inbox-confirm-slot">
          {pendingDeleteKey === deleteKey ? (
            <Button
              className="inbox-round-button inbox-round-button-danger"
              onClick={handleDelete}
              disabled={actionLoadingKey === actionKey}
              title="Confirm delete"
              aria-label="Confirm delete"
            >
              <Check size={15} />
            </Button>
          ) : null}
        </div>
      </>
    );
  };

  const renderInboxItem = (item) => {
    const isInvite = item.itemKind === "invite";
    const isConnection = item.itemKind === "connection";
    const isConnectionNotification =
      item.itemKind === "connectionNotification";
    const Icon = isInvite
      ? Users
      : isConnection
        ? MessageSquareText
        : isConnectionNotification
          ? getNotificationIcon(item.type)
        : getNotificationIcon(item.type);
    const isUnread = item.inboxStatus === "unread";
    const canViewCommunity =
      isInvite ||
      isConnection ||
      isConnectionNotification ||
      (item.communityId &&
        [
          "community_restored",
          "community_invite_accepted",
          "community_join_requested",
          "community_join_request_approved",
          "community_member_joined",
        ].includes(item.type));

    return (
      <article
        className="inbox-update-card"
        key={`${item.itemKind}:${item._id}`}
      >
        <div className="inbox-update-icon">
          <Icon size={18} />
        </div>
        <div
          className={`inbox-update-copy${isConnection ? " inbox-update-copy-note" : ""}`}
        >
          {isInvite ? (
            <>
              <div className="inbox-card-top">
                <span className="inbox-pill">
                  <Users size={14} />
                  Invite
                </span>
                <span className="inbox-date">
                  {formatDate(item.invitedAt)}
                </span>
              </div>
              <h3>{item.communityName}</h3>
              <p className="inbox-muted-line">
                Invited by {item.invitedBy?.displayName || "Community owner"}.
              </p>
            </>
          ) : (
            <>
              {isConnection ? (
                <p className="inbox-message-line">
                  <span className="inbox-message-date">
                    {formatDate(item.lastNoteAt)}
                  </span>
                  <span className="inbox-message-summary inbox-note-summary">
                    {item.otherUser?.publicDisplayName || "Someone"} sent you a
                    note
                    {item.lastContextCommunity
                      ? ` about ${item.lastContextCommunity.name}`
                      : ""}
                    .
                  </span>
                  {item.lastNotePreview ? (
                    <span className="inbox-message-summary inbox-note-preview">
                      {item.lastNotePreview}
                    </span>
                  ) : null}
                </p>
              ) : isConnectionNotification ? (
                <p className="inbox-message-line">
                  <span className="inbox-message-date">
                    {formatDate(item.createdAt)}
                  </span>
                  <span className="inbox-message-summary">
                    {item.message}
                  </span>
                </p>
              ) : (
                <p className="inbox-message-line">
                  <span className="inbox-message-date">
                    {formatDate(item.createdAt)}
                  </span>
                  <span className="inbox-message-summary">
                    {item.message}
                  </span>
                </p>
              )}
            </>
          )}
        </div>
        <div className="inbox-update-actions">
          {canViewCommunity ? (
            <Button
              className="inbox-round-button"
              onClick={() => handleViewItem(item, isInvite)}
              disabled={
                actionLoadingKey ===
                (isInvite
                  ? `read-invite:${item._id}`
                  : isConnection
                    ? `read-connection:${item._id}`
                    : isConnectionNotification
                      ? `read-connection-notification:${item._id}`
                    : `read:${item._id}`)
              }
              title="View"
              aria-label="View"
            >
              <ExternalLink size={15} />
            </Button>
          ) : null}
          {isInvite ? (
            <Button
              className="inbox-round-button inbox-round-button-success"
              onClick={() => handleAcceptInvite(item)}
              disabled={actionLoadingKey === `accept:${item._id}`}
              title="Accept invite"
              aria-label="Accept invite"
            >
              <Check size={15} />
            </Button>
          ) : null}
          {isUnread ? (
            <Button
              className="inbox-round-button"
              onClick={() =>
                isInvite
                  ? handleMarkInviteRead(item)
                  : isConnection
                    ? handleMarkConnectionRead(item)
                    : isConnectionNotification
                      ? handleMarkConnectionNotificationRead(item)
                  : handleMarkNotificationRead(item)
              }
              disabled={
                actionLoadingKey ===
                (isInvite
                  ? `read-invite:${item._id}`
                  : isConnection
                    ? `read-connection:${item._id}`
                    : isConnectionNotification
                      ? `read-connection-notification:${item._id}`
                    : `read:${item._id}`)
              }
              title="Mark read"
              aria-label="Mark read"
            >
              <MailOpen size={15} />
            </Button>
          ) : null}
          {renderDeleteControls(item)}
        </div>
      </article>
    );
  };

  const renderSection = ({ id, title, items }) => (
    <section className="inbox-section" id={id}>
      <div className={`inbox-section-header inbox-section-header-${id.replace("inbox-", "")}`}>
        <h2>
          {title}
          <span>{items.length}</span>
        </h2>
        {id === "inbox-unread" ? (
          <div className="inbox-section-actions inbox-section-actions-unread">
            <Button
              className="inbox-secondary-button"
              onClick={() => scrollToSection("inbox-read")}
            >
              Read
            </Button>
          </div>
        ) : null}
        {id === "inbox-read" && items.length > 0 ? (
          <div className="inbox-section-actions">
            <div className="inbox-delete-slot">
              <Button
                className="inbox-round-button inbox-round-button-danger"
                onClick={() => {
                  if (pendingBulkDeleteKey === "read") {
                    setPendingBulkDeleteKey(null);
                    return;
                  }
                  setPendingBulkDeleteKey("read");
                }}
                disabled={actionLoadingKey === "delete-read"}
                title={
                  pendingBulkDeleteKey === "read"
                    ? "Cancel delete all read"
                    : "Delete all read"
                }
                aria-label={
                  pendingBulkDeleteKey === "read"
                    ? "Cancel delete all read"
                    : "Delete all read"
                }
              >
                {pendingBulkDeleteKey === "read" ? (
                  <X size={15} />
                ) : (
                  <Trash2 size={15} />
                )}
              </Button>
            </div>
            <div className="inbox-confirm-slot">
              {pendingBulkDeleteKey === "read" ? (
                <Button
                  className="inbox-round-button inbox-round-button-danger"
                  onClick={handleDeleteReadMessages}
                  disabled={actionLoadingKey === "delete-read"}
                  title="Confirm delete all read"
                  aria-label="Confirm delete all read"
                >
                  <Check size={15} />
                </Button>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
      {items.length > 0 ? (
        <div className="inbox-section-flow">{items.map(renderInboxItem)}</div>
      ) : (
        <div className="inbox-update-card inbox-quiet-state">
          <div className="inbox-update-icon">
            <InboxIcon size={18} />
          </div>
          <p className="inbox-message-line">
            <span className="inbox-message-summary">
              No {title.toLowerCase()} messages.
            </span>
          </p>
        </div>
      )}
    </section>
  );

  return (
    <StudyLayout
      breadcrumbs={breadcrumbElement}
      buttons={<NavigationButtons />}
    >
      <div className="inbox-page">
        <section className="inbox-flow">
          {isEmpty ? (
            <section className="inbox-update-card inbox-quiet-state">
              <div className="inbox-update-icon">
                <InboxIcon size={18} />
              </div>
              <p className="inbox-message-line">
                <span className="inbox-message-summary">
                  Your inbox is clear.
                </span>
              </p>
            </section>
          ) : null}

          {renderSection({
            id: "inbox-unread",
            title: "Unread",
            items: inbox.unread,
          })}
          {renderSection({
            id: "inbox-read",
            title: "Read",
            items: inbox.read,
          })}

          <section className="inbox-about" aria-labelledby="inbox-about-title">
            <h2 id="inbox-about-title">About Inbox</h2>
            <p>
              Inbox shows recent community updates, invites, and connection
              notes. Older read items may be cleaned from Inbox over time, while
              the underlying community actions, memberships, and connection
              threads remain available where they belong.
            </p>
          </section>
        </section>
      </div>
    </StudyLayout>
  );
};

export default Inbox;
